import { useEffect, useState } from 'react';
import SEOHead from '@/components/SEOHead';
import { trackMetaPixelEvent } from '@/lib/meta-pixel';
import PricingHeroToggleV2 from '@/components/marketing/pricing/v2/PricingHeroToggleV2';
import PricingPlansGridV2 from '@/components/marketing/pricing/v2/PricingPlansGridV2';
import PaymentStripV2 from '@/components/marketing/pricing/v2/PaymentStripV2';
import PricingProfilesV2 from '@/components/marketing/pricing/v2/PricingProfilesV2';
import PricingCompareTableV2 from '@/components/marketing/pricing/v2/PricingCompareTableV2';
import PricingGuaranteesV2 from '@/components/marketing/pricing/v2/PricingGuaranteesV2';
import PricingFaqV2 from '@/components/marketing/pricing/v2/PricingFaqV2';
import { FinalCtaSection } from '@/components/marketing/v2/FinalCtaSection';
import { PricingContext, type BillingMode } from '@/components/marketing/pricing/v2/PricingContext';

const PLANS: { name: string; price: string }[] = [
  { name: 'Gratuit', price: '0' },
  { name: 'Starter', price: '5000' },
  { name: 'Croissance', price: '10000' },
  { name: 'Pro', price: '20000' },
];

const PRICING_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Velko POS',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web, Windows, macOS, Android',
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'XOF',
    lowPrice: '0',
    highPrice: '20000',
    offerCount: 5,
    offers: [
      // 4 paliers à prix fixe.
      ...PLANS.map((p) => ({
        '@type': 'Offer',
        name: `Plan ${p.name}`,
        price: p.price,
        priceCurrency: 'XOF',
        category: 'subscription',
      })),
      // 5e palier « Business » : tarif sur devis → pas de prix fixe (un Offer
      // schema.org reste valide sans `price`, avec name + description).
      {
        '@type': 'Offer',
        name: 'Plan Business',
        category: 'subscription',
        description: 'Tarif sur devis — grandes enseignes et besoins spécifiques.',
        priceCurrency: 'XOF',
        availability: 'https://schema.org/InStock',
      },
    ],
  },
};

export default function Pricing() {
  const [mode, setMode] = useState<BillingMode>('monthly');

  useEffect(() => {
    trackMetaPixelEvent('ViewContent', { content_name: 'pricing', content_category: 'plans' });
  }, []);

  return (
    <>
      <SEOHead
        title="Tarifs — 5 plans transparents dès 0 F CFA | Velko POS"
        description="Plans Velko : Gratuit, Starter 5K, Croissance 10K, Pro 20K et Business sur devis. Mode offline partout. Annulable à tout moment."
        path="/tarifs"
        ogImage="/brand/og/og-pricing.png"
        jsonLd={PRICING_JSON_LD}
        breadcrumbs={[
          { name: 'Accueil', path: '/' },
          { name: 'Tarifs', path: '/tarifs' },
        ]}
      />

      <PricingContext.Provider value={{ mode, setMode }}>
        <PricingHeroToggleV2 />
        <PricingPlansGridV2 />
      </PricingContext.Provider>

      <PaymentStripV2 />
      <PricingProfilesV2 />
      <PricingCompareTableV2 />
      <PricingGuaranteesV2 />
      <PricingFaqV2 />

      <FinalCtaSection
        eyebrow="Pas maintenant ?"
        title="Essaie maintenant."
        highlight="Paye plus tard."
        subtitle="14 jours d'essai sur Starter, Croissance et Pro, sans carte bancaire. Le plan Gratuit reste gratuit — aussi longtemps que tu veux."
        primaryLabel="Démarrer gratuitement"
        primaryTo="/register"
        secondaryLabel="Parler à un humain"
        secondaryTo="/contact"
        guarantees={['14 jours d’essai', 'Sans CB', 'Annulation à tout moment', 'Support FR']}
      />
    </>
  );
}
